import { useToast } from '@chakra-ui/react';
import axios from 'axios';
import debounce from 'lodash.debounce';
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../../../../hooks/auth';
import { listAppointmentsService } from '../../../../../../services/Appointments/ListAppointmentsService';
import { IDetailedAppointment } from '../../../../../../services/Appointments/ShowAppointmentsService';
import { translateError } from '../../../../../../utils/errors';
import {
  maskDateTime,
  maskMoney,
} from '../../../../../../utils/formatters/handleMask';
import {
  AppointmentTable,
  IAppointmentTableItem,
} from '../../components/AppointmentTable';

interface ILoadAppointmentsProps {
  userName?: string;
  page?: number;
}

export const AppointmentList = (): JSX.Element => {
  const toast = useToast();

  const { venture: authenticatedVenture } = useAuth();

  const [appointmentList, setAppointmentList] = useState<
    IAppointmentTableItem[]
  >([]);
  const [currentFilters, setCurrentFilters] =
    useState<{ [key: string]: string }>();
  const [currentPage, setCurrentPage] = useState(1);
  const [filterBy, setFilterBy] = useState('activityName');
  const [totalPages, setTotalPages] = useState<number>();

  const loadAppointments = useCallback(
    async ({ page, userName }: ILoadAppointmentsProps) => {
      try {
        const { items: appointments, pages } = await listAppointmentsService({
          page,
          userName,
        });

        const parsedAppointmentLists: IAppointmentTableItem[] =
          appointments.map((appointment) => ({
            ...appointment,
            formattedCreatedAt: maskDateTime(appointment.createdAt),
            formattedTotalValue: maskMoney(appointment.totalValue),
          }));

        setTotalPages(pages);
        setAppointmentList(parsedAppointmentLists);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: 'Falha ao carregar dados',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao carregar os dados das reservas, tente novamente.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    },
    [toast],
  );

  useEffect(() => {
    setTotalPages(1);
    setAppointmentList([]);

    loadAppointments({
      ...currentFilters,
      page: currentPage,
    });
  }, [currentPage, loadAppointments, currentFilters, authenticatedVenture]);

  const handleFilterBy = useCallback((value: string) => {
    setFilterBy(value);
  }, []);

  const handleFilter = useCallback(
    (value: string) => {
      setCurrentFilters(value ? { [filterBy]: value } : undefined);
    },
    [filterBy],
  );
  const debounceHandleFilter = debounce(handleFilter, 1000);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleAppointmentPayment = (
    paidAppointment: IDetailedAppointment,
  ): void => {
    setAppointmentList((prevState) =>
      prevState.map((productAppointmentList) =>
        productAppointmentList.id === paidAppointment.id
          ? {
              ...productAppointmentList,
              paymentStatus: paidAppointment.paymentStatus,
            }
          : productAppointmentList,
      ),
    );
  };

  const handleDelete = (appointment: IAppointmentTableItem): void => {
    setAppointmentList((prevState) =>
      prevState.filter((book) => book.id !== appointment.id),
    );
  };

  return (
    <AppointmentTable
      appointments={appointmentList}
      currentPage={currentPage}
      filterBy={filterBy}
      onChangeFilterBy={handleFilterBy}
      onDelete={handleDelete}
      onFilter={debounceHandleFilter}
      onPageChange={handlePageChange}
      onPay={handleAppointmentPayment}
      totalPages={totalPages}
    />
  );
};
